import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
const EXAMPLE_PROMPTS = [
    "8x12 Regal price",
    "all door series",
    "8x8 Vantage",
];
const formatMarkdown = (text) => {
    const parts = [];
    let remaining = text;
    let key = 0;
    while (remaining.length > 0) {
        const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
        if (boldMatch && boldMatch.index !== undefined) {
            if (boldMatch.index > 0) {
                parts.push(_jsx("span", { children: remaining.slice(0, boldMatch.index) }, key++));
            }
            parts.push(_jsx("strong", { className: "font-semibold text-gray-900", children: boldMatch[1] }, key++));
            remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
        }
        else {
            parts.push(_jsx("span", { children: remaining }, key++));
            break;
        }
    }
    return _jsx(_Fragment, { children: parts });
};
const PricingAssistantPage = () => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [language, setLanguage] = useState('en');
    const inputRef = useRef(null);
    const recognitionRef = useRef(null);
    const handleSubmit = async (query) => {
        const finalQuery = query || input.trim();
        if (!finalQuery || isLoading)
            return;
        const userMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: finalQuery,
            timestamp: new Date(),
        };
        setMessages(prev => [userMessage, ...prev]);
        setInput('');
        setIsLoading(true);
        try {
            const response = await fetch('/api/ai/pricing-query', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    question: finalQuery,
                    language,
                    conversationHistory: messages.slice(0, 4).map(m => ({
                        role: m.role,
                        content: m.content,
                    })),
                }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to get response');
            }
            const assistantMessage = {
                id: `assistant-${Date.now()}`,
                role: 'assistant',
                content: data.answer || data.error || 'No response received',
                data: data.data,
                totalRows: data.totalRows,
                timestamp: new Date(),
            };
            setMessages(prev => {
                const [latestUser, ...rest] = prev;
                return [latestUser, assistantMessage, ...rest];
            });
        }
        catch (err) {
            const errorMessage = {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: `Sorry, I encountered an error. Please try again.`,
                timestamp: new Date(),
            };
            setMessages(prev => {
                const [latestUser, ...rest] = prev;
                return [latestUser, errorMessage, ...rest];
            });
        }
        finally {
            setIsLoading(false);
            inputRef.current?.focus();
        }
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };
    const handleVoiceClick = async () => {
        // If already listening, stop and submit
        if (isListening && recognitionRef.current) {
            recognitionRef.current.stop();
            return;
        }
        if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
            alert('Voice recognition is not supported in your browser. Please use Chrome or Edge.');
            return;
        }
        try {
            await navigator.mediaDevices.getUserMedia({ audio: true });
        }
        catch (err) {
            alert('Microphone access is required for voice input. Please allow microphone access and try again.');
            return;
        }
        const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
        const recognition = new SpeechRecognition();
        recognitionRef.current = recognition;
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = language === 'es' ? 'es-MX' : 'en-US';
        let currentTranscript = '';
        recognition.onstart = () => {
            setIsListening(true);
            setInput('');
            currentTranscript = '';
        };
        recognition.onend = () => {
            setIsListening(false);
            recognitionRef.current = null;
            // Submit if we have a transcript
            if (currentTranscript.trim()) {
                handleSubmit(currentTranscript.trim());
            }
        };
        recognition.onerror = (event) => {
            setIsListening(false);
            recognitionRef.current = null;
            if (event.error === 'not-allowed') {
                alert('Microphone access was denied. Please enable it in your browser settings.');
            }
        };
        recognition.onresult = (event) => {
            const transcript = Array.from(event.results)
                .map((result) => result[0].transcript)
                .join('');
            currentTranscript = transcript;
            setInput(transcript);
            setIsFocused(true);
        };
        try {
            recognition.start();
        }
        catch (err) {
            setIsListening(false);
            recognitionRef.current = null;
        }
    };
    const formatDataTable = (data) => {
        if (!data || data.length === 0)
            return null;
        const columns = Object.keys(data[0]);
        return (_jsxs("div", { className: "mt-4 overflow-x-auto -mx-4 px-4", children: [_jsxs("table", { className: "w-full text-sm", children: [_jsx("thead", { children: _jsx("tr", { className: "border-b border-gray-100", children: columns.map(col => (_jsx("th", { className: "px-3 py-2 text-left font-medium text-gray-400 text-xs uppercase tracking-wider", children: col.replace(/_/g, ' ') }, col))) }) }), _jsx("tbody", { className: "divide-y divide-gray-50", children: data.slice(0, 10).map((row, idx) => (_jsx("tr", { children: columns.map(col => (_jsx("td", { className: "px-3 py-2 text-gray-700 whitespace-nowrap", children: typeof row[col] === 'number'
                                        ? (col.includes('price') || col.includes('cost') || col.includes('cogs'))
                                            ? `$${row[col].toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                            : row[col].toLocaleString()
                                        : row[col] ?? '—' }, col))) }, idx))) })] }), data.length > 10 && (_jsxs("p", { className: "text-xs text-gray-300 mt-3 text-center", children: ["Showing 10 of ", data.length, " results"] }))] }));
    };
    const hasMessages = messages.length > 0;
    return (_jsxs("div", { className: "h-screen bg-white flex flex-col overflow-hidden", children: [_jsxs("div", { className: "flex-shrink-0", children: [_jsxs("div", { className: "px-4 pt-4 pb-2 flex justify-between items-center", children: [_jsx("button", { onClick: () => navigate('/dashboard'), className: "text-gray-300 hover:text-gray-500 transition-colors p-2 -ml-2 rounded-full hover:bg-gray-50", children: _jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 1.5, d: "M10 19l-7-7m0 0l7-7m-7 7h18" }) }) }), _jsxs("div", { className: "flex items-center gap-4", children: [_jsxs("div", { className: "flex items-center bg-gray-100 rounded-full p-0.5", children: [_jsx("button", { onClick: () => setLanguage('en'), className: `px-3 py-1 text-xs font-medium rounded-full transition-all ${language === 'en'
                                                    ? 'bg-white text-gray-900 shadow-sm'
                                                    : 'text-gray-500 hover:text-gray-700'}`, children: "EN" }), _jsx("button", { onClick: () => setLanguage('es'), className: `px-3 py-1 text-xs font-medium rounded-full transition-all ${language === 'es'
                                                    ? 'bg-white text-gray-900 shadow-sm'
                                                    : 'text-gray-500 hover:text-gray-700'}`, children: "ES" })] }), _jsx("button", { onClick: logout, className: "text-gray-300 hover:text-gray-500 transition-colors text-sm", children: "Logout" })] })] }), _jsxs("div", { className: "text-center pt-4 pb-6", children: [_jsx("h1", { className: "text-3xl font-light text-gray-900 tracking-tight", children: "Ask PIA" }), !hasMessages && (_jsx("p", { className: "text-gray-500 mt-2 text-xs font-medium tracking-wide uppercase", children: "First-of-its-kind Enterprise Pricing AI" }))] }), _jsxs("div", { className: "flex items-center justify-center gap-4 pb-6", children: [_jsx("button", { onClick: handleVoiceClick, disabled: isLoading, title: isListening ? 'Click to stop and submit' : 'Click to start recording', className: `w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0
              ${isListening
                                    ? 'bg-red-500 text-white scale-110 shadow-lg shadow-red-200'
                                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'}
              ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
            `, children: isListening ? (
                                /* Stop icon when recording */
                                _jsx("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 24 24", children: _jsx("rect", { x: "6", y: "6", width: "12", height: "12", rx: "2" }) })) : (
                                /* Mic icon when not recording */
                                _jsxs("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 24 24", children: [_jsx("path", { d: "M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z" }), _jsx("path", { d: "M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" })] })) }), _jsxs("div", { className: `
              relative overflow-hidden transition-all duration-300 ease-out
              ${isFocused || input || isListening ? 'w-72 sm:w-80' : 'w-44'}
            `, children: [_jsx("input", { ref: inputRef, type: "text", value: input, onChange: (e) => setInput(e.target.value), onFocus: () => setIsFocused(true), onBlur: () => !input && !isListening && setIsFocused(false), onKeyDown: handleKeyDown, placeholder: isListening
                                            ? (language === 'es' ? "Grabando... clic para enviar" : "Recording... click stop to submit")
                                            : (isFocused
                                                ? (language === 'es' ? "Pregunta sobre precios..." : "Ask about pricing...")
                                                : (language === 'es' ? "Escribe o habla" : "Type or speak")), disabled: isLoading, className: `
                w-full bg-transparent border-0 border-b-2 
                ${isListening ? 'border-red-400' : (isFocused || input ? 'border-gray-900' : 'border-gray-200')}
                focus:outline-none focus:ring-0
                py-2 pr-8 text-gray-900 placeholder-gray-400 text-sm
                transition-all duration-300
                ${isLoading ? 'opacity-50' : ''}
                ${isListening ? 'placeholder-red-400' : ''}
              ` }), (input || isLoading) && (_jsx("button", { onClick: () => handleSubmit(), disabled: isLoading || !input.trim(), className: "absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors disabled:opacity-50", children: isLoading ? (_jsxs("svg", { className: "w-4 h-4 animate-spin", fill: "none", viewBox: "0 0 24 24", children: [_jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "2" }), _jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" })] })) : (_jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M14 5l7 7m0 0l-7 7m7-7H3" }) })) }))] })] }), !hasMessages && !isFocused && (_jsxs("div", { className: "flex items-center justify-center gap-2 text-xs text-gray-400 pb-6", children: [_jsx("span", { children: "Try:" }), EXAMPLE_PROMPTS.map((prompt, idx) => (_jsxs(React.Fragment, { children: [idx > 0 && _jsx("span", { className: "text-gray-200", children: "\u00B7" }), _jsxs("button", { onClick: () => handleSubmit(prompt), className: "hover:text-gray-600 transition-colors", children: ["\"", prompt, "\""] })] }, idx)))] }))] }), hasMessages && (_jsx("div", { className: "flex-1 overflow-y-auto", children: _jsxs("div", { className: "max-w-2xl mx-auto px-4 pb-8", children: [isLoading && (_jsxs("div", { className: "py-4", children: [_jsx("span", { className: "text-xs text-gray-300 mb-2 block", children: "PIA" }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("div", { className: "w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce", style: { animationDelay: '0ms' } }), _jsx("div", { className: "w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce", style: { animationDelay: '150ms' } }), _jsx("div", { className: "w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce", style: { animationDelay: '300ms' } })] })] })), messages.map((message, index) => (_jsx("div", { className: `py-4 ${index < messages.length - 1 ? 'border-b border-gray-50' : ''}`, children: message.role === 'user' ? (_jsxs("div", { className: "text-right", children: [_jsx("span", { className: "text-xs text-gray-300 mb-1 block", children: "You" }), _jsx("p", { className: "text-gray-900 text-sm", children: message.content })] })) : (_jsxs("div", { children: [_jsx("span", { className: "text-xs text-gray-300 mb-2 block", children: "PIA" }), _jsx("div", { className: "text-gray-600 text-sm leading-relaxed", children: message.content.split('\n').map((line, i) => (_jsxs(React.Fragment, { children: [i > 0 && _jsx("br", {}), formatMarkdown(line)] }, i))) }), message.data && message.data.length > 0 && formatDataTable(message.data)] })) }, message.id)))] }) })), !hasMessages && (_jsx("div", { className: "flex-shrink-0 py-4 text-center", children: _jsx("p", { className: "text-xs text-gray-200", children: "Powered by AI" }) }))] }));
};
export default PricingAssistantPage;
